// Formato y textos fijos de «Cobros». Las frases salen tal cual del mock.
import { formatDateMx, formatDayMx, formatTimeMx } from '@/lib/dateHelpers';
import { formatMxn, mxn } from '@/lib/dineroHelpers';
import { installmentDueDay } from '@/lib/installments';

export function money(cents: number): string {
    return formatMxn(mxn(Math.round(cents)));
}

/** Vencimiento de una cuota en `DD/MMM/YYYY`. */
export function dueDateLabel(dueDate: string | undefined): string {
    return dueDate ? formatDayMx(installmentDueDay(dueDate)) : '—';
}

export function instantLabel(iso: string): string {
    return formatDateMx(iso);
}

export const SEARCH_PLACEHOLDER = 'Buscar en mis créditos · o identificador (crédito, tel, IMEI)';
export const EMPTY_FILTER_TEXT = 'No tienes créditos en este filtro.';
export const WALK_IN_BANNER_TITLE = 'Crédito fuera de tu cartera';
export const WALK_IN_BANNER_TEXT = ' — cobro walk-in. Se registrará a tu tienda.';
export const NO_SELECTION_TITLE = 'Selecciona un crédito de la izquierda';
export const NO_SELECTION_TEXT = ' para ver el cupón a cobrar y registrar el pago.';
/** Sin `loan.credit.collection.write` no se ofrece registrar el pago. */
export const NO_SELECTION_TEXT_READ_ONLY = ' para ver el cupón a cobrar.';
export const SETTLED_TEXT = 'Crédito liquidado. Todos los cupones fueron pagados.';
export const HIGH_END_TITLE = 'Renta cartera 1% al cobrar';
export const NO_PAYMENTS_TEXT = 'Aún no hay cobros registrados.';

export const CREDIT_FORBIDDEN_NOTE =
    'Tu rol todavía no puede leer el estado de los créditos, así que no se muestran cupón ni saldo.';
export const CREDIT_ERROR_NOTE = 'No se pudo leer el estado de este crédito.';
export const SELLER_NOT_REGISTERED_NOTE =
    'Tu usuario no está dado de alta como vendedor. Contacta a tu administrador.';
export const COLLECTIONS_TRUNCATED_NOTE =
    'Tu cartera es más grande de lo que esta pantalla alcanza a leer: algunos créditos no aparecen.';
/** Placeholder cuando el usuario no puede buscar fuera de su cartera. */
export const SEARCH_PLACEHOLDER_MINE = 'Buscar en mis créditos por nombre, crédito o IMEI';
export const PAYMENTS_FORBIDDEN_NOTE = 'Tu rol todavía no puede leer los cobros de este crédito.';
export const PAYMENTS_ERROR_NOTE = 'No se pudieron leer los cobros de este crédito.';
export const WALK_IN_FORBIDDEN_NOTE =
    'Tu rol todavía no puede buscar créditos fuera de tu cartera.';
export const WALK_IN_ERROR_NOTE = 'No se pudo buscar fuera de tu cartera. Intenta de nuevo.';

/** Qué le falta o tiene mal al dato de la forma de pago del cobro. */
export const CHARGE_DETAIL_ERROR = {
    cardLast4: 'Escribe los últimos 4 dígitos de la tarjeta.',
    authCode: 'Escribe el número de autorización del voucher.',
    speiMissing: 'Escribe la clave de rastreo del SPEI.',
    speiTooLong: 'La clave de rastreo tiene máximo 30 caracteres.',
} as const;

/** Qué decirle al vendedor por cada code del cobro, en tuteo. */
export const CHARGE_ERROR_MESSAGE: Record<string, string> = {
    CUSTOMER_PAYMENT_AMOUNT_CHANGED: 'El monto cambió. Revisa el nuevo monto y confirma otra vez.',
    IN_STORE_PAYMENT_COLLECTION_IN_FLIGHT:
        'El cliente tiene un cobro automático en curso. Devuélvele el dinero y vuelve a intentar en unos minutos.',
    AD_HOC_CHARGE_PENDING: 'Hay otro pago en curso para este crédito. Intenta en unos minutos.',
    CREDIT_PLAN_BATCHES_PENDING:
        'El plan del crédito se está actualizando. Intenta en unos minutos.',
    CREDIT_OPERATION_IN_PROGRESS:
        'El crédito tiene una liquidación o reestructura en curso. Intenta más tarde.',
    CREDIT_NOT_COLLECTIBLE_IN_STORE:
        'Este crédito no se puede cobrar en tienda. Pide al cliente que contacte a cobranza.',
    TOO_MANY_OVERDUE_INSTALLMENTS:
        'El crédito tiene demasiadas cuotas vencidas para cobrarlas aquí. Escala el caso a cobranza.',
    PAYMENT_OPTION_UNAVAILABLE: 'Esa opción ya no está disponible. Elige otra.',
    IDEMPOTENCY_KEY_MISMATCH: 'El cobro cambió desde el último intento. Confirma otra vez.',
    INVALID_PAYMENT_DETAIL: 'Revisa el dato de la forma de pago.',
    STORE_NOT_IN_SELLER_RETAILER: 'Tu tienda no pertenece a tu cadena. Contacta al administrador.',
    SELLER_NOT_REGISTERED:
        'Tu usuario no está dado de alta como vendedor. Contacta al administrador.',
    PERMISSION_DENIED: 'Tu rol no puede cobrar en tienda.',
    CREDIT_NOT_FOUND: 'No encontramos ese crédito en tu cadena. Revisa el dato.',
    COLLECTION_ENGINE_UNAVAILABLE:
        'No pudimos confirmar con el sistema de cobro. Intenta de nuevo.',
    SELLER_IDENTITY_LOOKUP_FAILED: 'No pudimos confirmar tu usuario. Intenta de nuevo.',
    STORE_ROSTER_LOOKUP_FAILED: 'No pudimos confirmar tu tienda. Intenta de nuevo.',
};
export const CHARGE_ERROR_FALLBACK = 'No se pudo registrar el cobro. Intenta de nuevo.';
/** Sin respuesta del servidor el cobro pudo asentarse: se reintenta con la misma llave. */
export const CHARGE_UNCERTAIN_MESSAGE =
    'No pudimos confirmar si el cobro quedó registrado. Vuelve a intentar: no se cobrará dos veces.';

/** Al volver a un crédito con un cobro sin respuesta: el vendedor dice si es ese o uno nuevo. */
export function pendingChargeNotice(amountCents: number, createdAt: number): string {
    return `Tienes un cobro sin confirmar de ${money(amountCents)} a las ${formatTimeMx(new Date(createdAt).toISOString())}.`;
}
export const PENDING_CHARGE_HINT =
    'Si es el mismo dinero, reintenta ese cobro: no se cobrará dos veces. Si el cliente paga otra vez, regístralo como cobro nuevo.';

/** Rótulos del resumen del crédito arriba del cobro (opción A). */
export const CHARGE_SUMMARY_LABEL = {
    total: 'Total del crédito',
    paid: 'Ya pagó',
    remaining: 'Le falta',
    next: 'Próxima cuota',
} as const;

export const CHARGE_TITLE = 'Cobrar';
export const CHARGE_STEP_LABEL = {
    amount: 'Cuánto',
    method: 'Cómo',
    confirm: 'Confirmar',
} as const;
export const CHARGE_AMOUNT_LEGEND = '¿Cuánto paga?';
export const CHARGE_METHOD_LEGEND = '¿Cómo pagó?';

/** Cada tipo de cobro como lo elige el vendedor. */
export const CHARGE_KIND_TITLE = {
    NEXT_INSTALLMENT: 'Su cuota',
    ADVANCE: 'Adelantar cuotas',
    PREPAYMENT: 'Abono a capital',
    SETTLEMENT: 'Liquidar',
} as const;

/** Por qué una opción no se puede cobrar hoy. */
export const CHARGE_UNAVAILABLE_LABEL: Record<string, string> = {
    PAYMENT_IN_PROGRESS: 'Hay un pago u operación en curso; vuelve a intentar más tarde.',
    CREDIT_NOT_CURRENT: 'El crédito tiene pagos vencidos; cobra primero su cuota.',
    USE_SETTLEMENT: 'Cubriría todo lo que falta: usa «Liquidar».',
    NOT_AVAILABLE_YET: 'Esta opción no está disponible para este crédito.',
    CREDIT_CLOSED: 'El crédito ya está cerrado.',
};
export const CHARGE_UNAVAILABLE_FALLBACK = 'No disponible.';

export const CHARGE_FIELD_LABEL = {
    installmentCount: 'Cuántas cuotas',
    prepaymentAmount: 'Monto del abono',
    cardLast4: 'Últimos 4 dígitos',
    authCode: 'Autorización',
    speiTrackingKey: 'Clave de rastreo',
} as const;

export const CHARGE_BUTTON = {
    next: 'Siguiente',
    back: 'Atrás',
    confirm: 'Confirmar cobro',
    restart: 'Volver a intentar',
    fixDetail: 'Corregir el dato',
    changeAmount: 'Elegir otro monto',
    another: 'Registrar otro cobro',
    retryPending: 'Reintentar ese cobro',
    newCharge: 'Es un cobro nuevo',
} as const;

export const CHARGE_CONFIRM_LABEL = {
    kind: 'Qué paga',
    amount: 'Monto',
    method: 'Forma de pago',
} as const;
export const CHARGE_CONFIRM_NOTE =
    'Recibe el dinero antes de confirmar. El cobro se registra a tu tienda.';
/** La mutación quedó en pausa porque el navegador no tiene red. */
export const CHARGE_OFFLINE_NOTE = 'Sin conexión: el cobro se enviará al reconectar.';
/** 404 `CREDIT_NOT_FOUND` de las opciones: el crédito no es de la cadena del vendedor. */
export const CHARGE_OUT_OF_CHAIN_NOTE =
    'Este crédito no es de una tienda de tu cadena; no se puede cobrar aquí.';
export const CHARGE_OPTIONS_ERROR = 'No se pudieron leer las opciones de cobro de este crédito.';

/** «Cuotas #2 y #3 · 1 vencida»: qué cubre «Su cuota». */
export function nextInstallmentDetail(
    numbers: number[] | undefined,
    overdueCount: number | undefined,
): string {
    const list = (numbers ?? []).map((n) => `#${String(n)}`);
    const which =
        list.length > 1
            ? `Cuotas ${list.slice(0, -1).join(', ')} y ${String(list.at(-1))}`
            : list.length === 1
              ? `Cuota ${list[0]}`
              : '';
    const overdue = overdueCount ?? 0;
    const late = overdue > 0 ? `${String(overdue)} vencida${overdue === 1 ? '' : 's'}` : '';
    return [which, late].filter(Boolean).join(' · ');
}

export function cardDetailText(cardLast4: string, authCode: string): string {
    return `Terminación ${cardLast4} · autorización ${authCode}`;
}

export function speiDetailText(speiTrackingKey: string): string {
    return `Clave de rastreo ${speiTrackingKey}`;
}

export function installmentCountLabel(count: number): string {
    return `${String(count)} cuota${count === 1 ? '' : 's'}`;
}

export function rangeText(min: string, max: string): string {
    return `De ${min} a ${max}`;
}

export function prepaymentRangeError(min: string, max: string): string {
    return `El abono va de ${min} a ${max}.`;
}

/** Rótulos del comprobante en pantalla. */
export const RECEIPT_LABEL = {
    title: 'Cobro registrado',
    folio: 'Folio',
    amount: 'Monto',
    kind: 'Qué pagó',
    method: 'Forma de pago',
    appliedAt: 'Fecha y hora',
    store: 'Tienda',
    seller: 'Cobró',
    balanceAfter: 'Le falta',
    nextInstallment: 'Siguiente cuota',
    device: 'Equipo',
} as const;
export const RECEIPT_NO_NEXT_INSTALLMENT = 'Sin cuotas pendientes';
export const RECEIPT_UNKNOWN = '—';
